import { useEffect, useState } from 'react';
import { api } from '../lib/api';

export default function Pros({ isAdmin }) {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setPros(await api.listPros());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!confirm(`Delete ${p.player_name}'s swing?`)) return;
    await api.deletePro(p.id);
    load();
  };

  return (
    <div className="max-w-[1200px] mx-auto p-4">
      <div className="flex items-center justify-between pb-3 border-b border-fairway-800 mb-6">
        <div>
          <h1 className="display text-2xl text-cream-100">Pro library</h1>
          <p className="text-xs text-ink-400 mt-0.5">
            Reference swings to compare against.
            {!isAdmin && ' (Read-only — admin adds new entries.)'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setUploadOpen(true)}
            className="px-4 py-2 bg-cream-100 text-fairway-950 rounded text-sm font-medium hover:bg-cream-200 transition"
          >
            + Add pro swing
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-ink-400 text-sm">Loading…</div>
      ) : pros.length === 0 ? (
        <div className="text-center py-16">
          <p className="display text-lg text-ink-200">No pros in the library yet.</p>
          {isAdmin && <p className="text-sm text-ink-400 mt-2">Click + Add pro swing to seed the library.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {pros.map(p => (
            <div key={p.id} className="bg-fairway-900 border border-fairway-800 rounded-lg overflow-hidden">
              <div className="aspect-[9/16] bg-black">
                <video
                  src={api.streamUrl(p.r2_key)}
                  preload="metadata"
                  muted
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-3 space-y-1">
                <div className="text-sm text-cream-100 font-medium">{p.player_name}</div>
                <div className="text-[11px] text-ink-400 flex gap-2">
                  {p.club && <span>{p.club}</span>}
                  {p.view_angle && <span>· {p.view_angle}</span>}
                </div>
                {p.notes && <div className="text-[11px] text-ink-300 line-clamp-2 pt-1">{p.notes}</div>}
                {isAdmin && (
                  <button
                    onClick={() => del(p)}
                    className="text-[10px] uppercase tracking-wider text-accent-flag hover:underline pt-2"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadOpen && (
        <ProUploadModal onClose={() => setUploadOpen(false)} onUploaded={() => { setUploadOpen(false); load(); }} />
      )}
    </div>
  );
}

function ProUploadModal({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [club, setClub] = useState('');
  const [viewAngle, setViewAngle] = useState('');
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const upload = async () => {
    if (!file || !playerName.trim()) { setErr('Player name and video required'); return; }
    setErr('');
    setUploading(true);
    try {
      await api.uploadPro(file, { player_name: playerName, club, view_angle: viewAngle, notes }, setProgress);
      onUploaded();
    } catch (e) {
      setErr(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-fairway-900 border border-fairway-700 rounded-lg w-full max-w-md p-5 space-y-3"
      >
        <h2 className="display text-lg text-cream-100">Add a pro swing</h2>

        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Player name</span>
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="e.g. Scottie Scheffler"
            className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Club</span>
            <select
              value={club}
              onChange={(e) => setClub(e.target.value)}
              className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
            >
              <option value="">—</option>
              <option>Driver</option>
              <option>7-iron</option>
              <option>Wedge</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">View</span>
            <select
              value={viewAngle}
              onChange={(e) => setViewAngle(e.target.value)}
              className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
            >
              <option value="">—</option>
              <option>Down-the-line</option>
              <option>Face-on</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Notes (optional)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
          />
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Video file</span>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setFile(e.target.files[0])}
            className="block w-full text-sm text-ink-200 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-fairway-800 file:text-ink-100 file:text-xs file:uppercase hover:file:bg-fairway-700 cursor-pointer"
          />
        </label>

        {uploading && (
          <div>
            <div className="h-2 bg-fairway-800 rounded overflow-hidden">
              <div className="h-full bg-cream-200 transition-all" style={{ width: `${progress * 100}%` }} />
            </div>
            <div className="text-[11px] text-ink-400 mt-1 monospace">{Math.round(progress * 100)}%</div>
          </div>
        )}

        {err && <div className="text-accent-flag text-xs">{err}</div>}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 px-3 py-1.5 bg-fairway-800 rounded text-xs uppercase tracking-wider hover:bg-fairway-700 transition">
            Cancel
          </button>
          <button
            onClick={upload}
            disabled={uploading}
            className="flex-1 px-3 py-1.5 bg-cream-100 text-fairway-950 rounded text-xs uppercase tracking-wider hover:bg-cream-200 disabled:opacity-30 transition"
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
